/*
  ExerciseProgramsPanel — Stage 1 of the Exercise Program spec.
  Sits inside every client's Health Record tab.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCanonicalClient } from "@/lib/canonicalClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dumbbell, Plus, Upload, Loader2, Copy, Trash2, Download, Printer,
  AlertTriangle, Cloud, Sparkles, Wand2, UserPlus,
} from "lucide-react";
import ExerciseProgramEditor from "./ExerciseProgramEditor";
import GDrivePickerDialog from "./GDrivePickerDialog";
import TemplatePickerDialog from "./TemplatePickerDialog";
import TemplateManageDialog from "./TemplateManageDialog";
import CopyProgramToClientDialog from "./CopyProgramToClientDialog";
import { printPdfFromUrl } from "@/lib/printPdf";

const STATUS_TONE = {
  imported:            "bg-zinc-100 text-zinc-700 border-zinc-300",
  draft:               "bg-blue-50 text-blue-800 border-blue-300",
  awaiting_approval:   "bg-amber-50 text-amber-800 border-amber-300",
  active:              "bg-emerald-50 text-emerald-800 border-emerald-300",
  review_due:          "bg-orange-50 text-orange-800 border-orange-300",
  superseded:          "bg-zinc-100 text-zinc-500 border-zinc-300",
  completed:           "bg-zinc-100 text-zinc-600 border-zinc-300",
  archived:            "bg-zinc-100 text-zinc-400 border-zinc-300",
};

const STATUS_LABEL = {
  imported: "Imported",
  draft: "Draft",
  awaiting_approval: "Awaiting approval",
  active: "Active",
  review_due: "Review due",
  superseded: "Superseded",
  completed: "Completed",
  archived: "Archived",
};


export default function ExerciseProgramsPanel({ email }) {
  const canonical = useCanonicalClient();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [gdriveOpen, setGdriveOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  // Cross-client copy — target picker dialog. `copySource` is the
  // whole program row (title + client_email) so the dialog can show
  // it in the header and disable "same client" picks.
  const [copySource, setCopySource] = useState(null);
  const { can } = useAuth();
  const canManageTemplates = can ? can("owner", "manager") : true;
  const fileInput = useRef(null);
  const enc = encodeURIComponent(email || "");

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      // Phase-2 scoped filter — when the parent hub is in `?as=<cid>`
      // mode, narrow the list to just that canonical client's programs.
      const params = canonical?.clientId
        ? { canonical_client_id: canonical.clientId } : undefined;
      const r = await api.get(`/health-record/${enc}/exercise-programs`,
                              params ? { params } : undefined);
      setRows(r.data?.programs || []);
    } catch (e) {
      toast.error("Couldn't load exercise programs", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [email, enc, canonical?.clientId]);

  useEffect(() => { load(); }, [load]);

  async function createBlank() {
    try {
      const r = await api.post(`/health-record/${enc}/exercise-programs`, { title: "New exercise program" });
      toast.success("Draft program created");
      setOpenId(r.data.id);
      await load();
    } catch (e) {
      toast.error("Couldn't create program", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function handleFiles(files) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const form = new FormData();
        form.append("file", f);
        form.append("service_type", "exercise_physiology");
        await api.post(
          `/health-record/${enc}/exercise-programs/import`,
          form, { headers: { "Content-Type": "multipart/form-data" } },
        );
      }
      toast.success(`${files.length} program(s) imported — review and finalise.`);
      await load();
    } catch (e) {
      toast.error("Import failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function duplicate(id) {
    try {
      const r = await api.post(`/exercise-programs/${id}/duplicate`);
      toast.success("Duplicated as new draft");
      setOpenId(r.data.id);
      await load();
    } catch (e) {
      toast.error("Duplicate failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function remove(id) {
    if (!confirm("Archive this program? The original file remains on file.")) return;
    try {
      await api.delete(`/exercise-programs/${id}`);
      toast.success("Archived");
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <Card className="p-4 rounded-lg border border-zinc-200 shadow-none" data-testid="exercise-programs-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Dumbbell className="h-4 w-4 text-indigo-700" />
          <h3 className="font-medium text-sm">Exercise programs</h3>
          <span className="text-xs text-zinc-500">
            Import from DOCX or build in-app. Clinical safety rules run on every activation.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => setTemplateOpen(true)}
            data-testid="ep-template-btn"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1" /> From template
          </Button>
          {canManageTemplates && (
            <Button
              size="sm" variant="outline"
              onClick={() => setManageOpen(true)}
              data-testid="ep-manage-templates-btn"
              title="Edit any program template's sections, exercises, precautions and defaults — changes apply to future programs instantiated from it."
            >
              <Wand2 className="h-3.5 w-3.5 mr-1" /> Edit templates
            </Button>
          )}
          <Button
            size="sm" variant="outline"
            onClick={() => setGdriveOpen(true)}
            data-testid="ep-gdrive-btn"
          >
            <Cloud className="h-3.5 w-3.5 mr-1" /> Google Drive
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            data-testid="ep-import-btn"
          >
            {uploading
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Importing…</>
              : <><Upload className="h-3.5 w-3.5 mr-1" /> Import DOCX</>
            }
          </Button>
          <Button size="sm" onClick={createBlank} data-testid="ep-new-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> New program
          </Button>
          <input
            ref={fileInput} type="file" multiple
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">
          <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">
          No programs yet. Import a DOCX or start a new blank program.
        </div>
      ) : (
        <div className="divide-y divide-zinc-200">
          {rows.map((r) => (
            <div
              key={r.id}
              className="py-2.5 flex items-center gap-3"
              data-testid={`ep-row-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-900 truncate">
                  {r.title || "Untitled"}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  v{r.version || 1} · {r.exercise_count || 0} exercises · {r.section_count || 0} sections
                  {r.service_type ? ` · ${r.service_type.replace(/_/g, " ")}` : ""}
                </div>
                {(r.tags || []).length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {r.tags.map((t) => (
                      <span key={t} className="text-2xs uppercase px-1.5 py-0.5 rounded border bg-white border-zinc-200 text-zinc-700">{t}</span>
                    ))}
                  </div>
                )}
              </div>
              <Badge className={`text-2xs uppercase border ${STATUS_TONE[r.status] || STATUS_TONE.draft}`}>
                {STATUS_LABEL[r.status] || r.status}
              </Badge>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => setOpenId(r.id)} data-testid={`ep-open-${r.id}`}>
                  Open
                </Button>
                <Button size="sm" variant="ghost" onClick={() => duplicate(r.id)} title="Duplicate on this client" data-testid={`ep-dup-${r.id}`}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost"
                        onClick={() => setCopySource(r)}
                        title="Copy to another client's record"
                        data-testid={`ep-copy-to-client-${r.id}`}>
                  <UserPlus className="h-3.5 w-3.5" />
                </Button>
                <a
                  href={`/api/exercise-programs/${r.id}/pdf?layout=worksheet`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center h-8 px-2 rounded hover:bg-zinc-100 text-2xs font-medium border border-zinc-200"
                  title="Download worksheet-style PDF (client tracking sheet)"
                  data-testid={`ep-pdf-worksheet-${r.id}`}
                >
                  <Download className="h-3 w-3 mr-1" /> Worksheet
                </a>
                <button
                  onClick={() => printPdfFromUrl(`/api/exercise-programs/${r.id}/pdf?layout=worksheet`).catch((e) => toast.error("Print failed", { description: e.message }))}
                  className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-zinc-100 text-zinc-600 border border-zinc-200"
                  title="Print worksheet-style PDF"
                  data-testid={`ep-print-worksheet-${r.id}`}
                >
                  <Printer className="h-3 w-3" />
                </button>
                <a
                  href={`/api/exercise-programs/${r.id}/pdf?layout=compact`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center h-8 px-2 rounded hover:bg-zinc-100 text-2xs font-medium border border-zinc-200"
                  title="Download compact client-facing PDF"
                  data-testid={`ep-pdf-compact-${r.id}`}
                >
                  <Download className="h-3 w-3 mr-1" /> Compact
                </a>
                <button
                  onClick={() => printPdfFromUrl(`/api/exercise-programs/${r.id}/pdf?layout=compact`).catch((e) => toast.error("Print failed", { description: e.message }))}
                  className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-zinc-100 text-zinc-600 border border-zinc-200"
                  title="Print compact client-facing PDF"
                  data-testid={`ep-print-compact-${r.id}`}
                >
                  <Printer className="h-3 w-3" />
                </button>
                <Button size="sm" variant="ghost" onClick={() => remove(r.id)} title="Archive" data-testid={`ep-del-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {openId && (
        <ExerciseProgramEditor
          programId={openId}
          open={!!openId}
          onOpenChange={(v) => { if (!v) { setOpenId(null); load(); } }}
        />
      )}
      <GDrivePickerDialog
        open={gdriveOpen}
        onOpenChange={setGdriveOpen}
        email={email}
        canonicalClientId={canonical?.clientId || null}
        onImported={load}
      />
      <TemplatePickerDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        email={email}
        onCreated={(program) => { load(); setOpenId(program.id); }}
      />
      <TemplateManageDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        onChanged={load}
      />
      <CopyProgramToClientDialog
        open={!!copySource}
        onOpenChange={(v) => { if (!v) setCopySource(null); }}
        sourceProgram={copySource ? { ...copySource, client_email: email } : null}
        onCopied={() => setCopySource(null)}
      />
    </Card>
  );
}
